"use client";

import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getCheckoutSession,
  submitPayment,
  cancelPayment,
  getPaymentStatus,
} from "@/lib/api/checkout";
import type {
  CheckoutSession,
  SubmitPaymentParams,
} from "@/lib/api/checkout.schema";

// Custom error class to include status code and error message
export class CheckoutError extends Error {
  statusCode: number;
  errorMessage: string;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "CheckoutError";
    this.statusCode = statusCode;
    this.errorMessage = message;
  }
}

export function useCheckoutSession(
  token: string,
  initialData?: CheckoutSession,
) {
  return useQuery({
    queryKey: ["checkout", token],
    queryFn: async () => {
      const result = await getCheckoutSession(token);
      if (result.error) {
        throw new CheckoutError(result.error, result.statusCode || 500);
      }
      return result.data!;
    },
    initialData,
    enabled: !!token,
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function usePaymentStatus(token: string) {
  return useQuery({
    queryKey: ["checkout-status", token],
    queryFn: async () => {
      const result = await getPaymentStatus(token);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
    enabled: !!token,
    retry: false,
    staleTime: 5 * 1000,
  });
}

export function useSubmitPayment(token: string) {
  return useMutation({
    mutationFn: async (params: SubmitPaymentParams) => {
      const result = await submitPayment(token, params);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
  });
}

export function useCancelPayment(token: string) {
  return useMutation({
    mutationFn: async (attemptId: string) => {
      const result = await cancelPayment(token, attemptId);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
  });
}
